import type { Factory } from "../Factory"
import { Listenable } from "../Listenable"
import { Configuration as RecordConfiguration } from "./Configuration"

const promise = new Promise(resolve => resolve(true))
export type Record<T> = T & Listenable<Required<Record.ListenableParameters<T>>> & Record.Symbol
export namespace Record {
	const symbol: unique symbol = Symbol("Record")
	export type Symbol = globalThis.Record<typeof symbol, typeof symbol>
	export import Configuration = RecordConfiguration
	export type ListenableParameters<T> = {
		[Property in keyof T | "*"]: Property extends keyof T
			? [value: T[Property], event: Property]
			: [value: T[keyof T], event: keyof T]
	}
	export type FactoryReturn<T> = {
		result: Record<T>
		backend: () => T
		listeners: Listenable.Listeners<ListenableParameters<T>>
	}

	export function create<T>(configuration: Configuration<Record<T>>, target: T): Record<T>
	export function create<T>(
		configuration: Configuration<T>,
		target: T,
		factory: Factory<Listenable<any>>
	): FactoryReturn<T>
	export function create<T extends globalThis.Record<keyof any, any>>(
		configuration: Configuration<T>,
		target: T,
		factory?: Factory<Listenable<any>>
	): Record<T> | FactoryReturn<T> {
		const listeners = Listenable.Listeners.create<ListenableParameters<T>>()
		const controllers = new Map<(...args: any) => any, Listenable.Controller>()
		const backend = Object.defineProperties(target, {
			[symbol]: {
				enumerable: false,
				configurable: true,
				writable: false,
				value: symbol,
			},
			listen: {
				enumerable: false,
				configurable: true,
				writable: false,
				value: <E extends keyof ListenableParameters<T>>(
					event: E,
					listener: (value: T[E], event: E) => void,
					options?: Listenable.Options
				): Listenable.Controller => {
					const controller = listeners.add(event, listener as any, options)
					controllers.set(listener, controller)
					if (!options?.passive)
						if (event === "*") {
							listeners.call(event, "read", ...([backend[event], event] as any))
							entries(backend).forEach(([key, value]: [any, any]) => listener(value, key))
						} else {
							listener(result[event], event)
							listeners.call(event, "read", ...([backend[event], event] as any))
						}
					return controller
				},
			},
			unlisten: {
				enumerable: false,
				configurable: true,
				writable: false,
				value: (listener: (...argument: any[]) => any) => {
					controllers.get(listener)?.abort()
					controllers.delete(listener)
				},
			},
		}) as Record<T>
		const result = new Proxy<Record<T>>(backend, {
			get(backend: T, p: keyof any, state: Record<T>): T[keyof T] | undefined {
				const property = p as T[keyof T]
				const current = backend[property]
				let result: T[keyof T] | undefined = current
				if (property === "*" || property in promise)
					result = undefined
				else if (property === "listen" || property === "unlisten")
					result = backend[property]
				else if (current === undefined) {
					result = configuration.initiate?.({ state: factory?.state, me: state, property, current })
					if (result !== backend[property])
						state[property] = result as any
					configuration.load?.({ state: factory?.state, me: state, property, current }).then(result => {
						if (result !== backend[property])
							state[property] = result as any
					})
					listeners.call(property, "read", ...([backend[property], property] as any))
					listeners.call("*", "read", ...([backend[property], property] as any))
				}
				return result
			},
			set(backend: T, p: keyof any, value: T[keyof T], state: Record<T>): boolean {
				const property = p as T[keyof T]
				if (property === "*")
					return false
				;(
					configuration.store?.({ state: factory?.state, me: state, property, current: backend[property], value }) ??
					Promise.resolve(value)
				).then(result => {
					if (result !== backend[property]) {
						backend[property] = result as any
						listeners.call(property, "write", ...([result, property] as any))
						listeners.call("*", "write", ...([result, property] as any))
					}
				})
				return true
			},
		})
		return !factory ? result : { result, backend: () => backend, listeners }
	}
	export function is(value: unknown): value is Record<globalThis.Record<string | number, unknown>> {
		return typeof value === "object" && !!value && symbol in value && value[symbol] === symbol
	}
	export function entries<T>(
		object: Record<T> | T | undefined
	): [
		Exclude<keyof T, keyof Listenable<ListenableParameters<T>> | keyof Symbol>,
		T[Exclude<keyof T, keyof Listenable<ListenableParameters<T>> | keyof Symbol>]
	][] {
		return object == undefined ? [] : (globalThis.Object.entries(object) as ReturnType<typeof entries>)
	}
	export function values<T>(
		object: Record<T> | T | undefined
	): T[Exclude<keyof T, keyof Listenable<ListenableParameters<T>> | keyof Symbol>][] {
		return object == undefined ? [] : (globalThis.Object.values(object) as ReturnType<typeof values>)
	}
	export function keys<T>(
		object: Record<T> | T | undefined
	): Exclude<keyof T, keyof Listenable<ListenableParameters<T> | keyof Symbol>>[] {
		return object == undefined ? [] : (globalThis.Object.keys(object) as ReturnType<typeof keys>)
	}
}
